/**
 * v0.19.0 — Esteira e Fortalecimento: atividade só com tempo (sem GPS, sem percurso).
 * Lógica pura (sem DOM, sem relógio próprio, sem rede). O cronômetro usa carimbos de horário, então continua certo
 * com a tela bloqueada. A cópia no aparelho e o reenvio ficam em `timedStore` (funções que recebem o `Storage`).
 */

export type TimedSport = "treadmill" | "strength";
export type TimedStatus = "running" | "paused" | "finalizing";

export interface Timed {
  clientId: string;
  sport: TimedSport;
  status: TimedStatus;
  startedAt: number;          // ms desde a época
  activeMs: number;           // tempo ativo já fechado
  segStart: number | null;    // início do trecho em andamento
  finishedAt: number | null;
}

export const MIN_TIMED_S = 10;   // menos que isso é toque sem querer

export const startTimed = (sport: TimedSport, clientId: string, now: number): Timed =>
  ({ clientId, sport, status: "running", startedAt: now, activeMs: 0, segStart: now, finishedAt: null });

const close = (t: Timed, now: number): Timed =>
  t.segStart === null ? t : { ...t, activeMs: t.activeMs + Math.max(0, now - t.segStart), segStart: null };

export const pauseTimed = (t: Timed, now: number): Timed => (t.status === "running" ? { ...close(t, now), status: "paused" } : t);
export const resumeTimed = (t: Timed, now: number): Timed => (t.status === "paused" ? { ...t, status: "running", segStart: now } : t);
export const finishTimed = (t: Timed, now: number): Timed =>
  t.status === "finalizing" ? t : { ...close(t, now), status: "finalizing", finishedAt: now };

export const timedActiveMs = (t: Timed, now: number): number => t.activeMs + (t.segStart !== null ? Math.max(0, now - t.segStart) : 0);
export const timedElapsedMs = (t: Timed, now: number): number => Math.max(0, (t.finishedAt ?? now) - t.startedAt);
export const timedLongEnough = (t: Timed): boolean => t.activeMs >= MIN_TIMED_S * 1000;

export interface TimedBody {
  client_id: string;
  sport: TimedSport;
  started_at: string;
  moving_time_s: number;
  elapsed_time_s: number;
}

/** Corpo de `POST /me/timed-recordings`; null se a gravação ainda não terminou ou é curta demais. */
export function buildTimedBody(t: Timed): TimedBody | null {
  if (t.status !== "finalizing" || t.finishedAt === null || !timedLongEnough(t)) return null;
  const elapsed = Math.max(1, Math.ceil((t.finishedAt - t.startedAt) / 1000));
  const moving = Math.min(elapsed, Math.max(1, Math.round(t.activeMs / 1000)));
  return { client_id: t.clientId, sport: t.sport, started_at: new Date(t.startedAt).toISOString(), moving_time_s: moving, elapsed_time_s: elapsed };
}

// ----------------------------------------------------------------------------- cópia no aparelho

const SESSION_KEY = (uid: string) => `eapace.timed.session.${uid}`;
const OUTBOX_KEY = (uid: string) => `eapace.timed.outbox.${uid}`;
const OUTBOX_MAX_AGE_MS = 29 * 24 * 3600 * 1000;   // o servidor recusa gravações com mais de 30 dias

type Store = Pick<Storage, "getItem" | "setItem" | "removeItem">;

export function loadTimed(s: Store, uid: string): Timed | null {
  try {
    const v = JSON.parse(s.getItem(SESSION_KEY(uid)) ?? "null") as Timed | null;
    if (!v || typeof v.clientId !== "string" || (v.sport !== "treadmill" && v.sport !== "strength") || !Number.isFinite(v.startedAt)) return null;
    return v;
  } catch { return null; }
}
export function saveTimed(s: Store, uid: string, t: Timed | null): void {
  try { if (t) s.setItem(SESSION_KEY(uid), JSON.stringify(t)); else s.removeItem(SESSION_KEY(uid)); } catch { /* sem armazenamento: segue só na memória */ }
}

export function loadOutbox(s: Store, uid: string, now: number): TimedBody[] {
  try {
    const v = JSON.parse(s.getItem(OUTBOX_KEY(uid)) ?? "[]") as TimedBody[];
    return Array.isArray(v) ? v.filter((b) => b && Date.parse(b.started_at) > now - OUTBOX_MAX_AGE_MS) : [];
  } catch { return []; }
}
function writeOutbox(s: Store, uid: string, list: TimedBody[]): void {
  try { if (list.length) s.setItem(OUTBOX_KEY(uid), JSON.stringify(list)); else s.removeItem(OUTBOX_KEY(uid)); } catch { /* idem */ }
}
export function addToOutbox(s: Store, uid: string, body: TimedBody, now: number): void {
  const list = loadOutbox(s, uid, now).filter((b) => b.client_id !== body.client_id);
  writeOutbox(s, uid, [...list, body]);
}
export function removeFromOutbox(s: Store, uid: string, clientId: string, now: number): void {
  writeOutbox(s, uid, loadOutbox(s, uid, now).filter((b) => b.client_id !== clientId));
}

export interface TimedPostResult { status: number; activityId?: string }
export type TimedPost = (body: TimedBody) => Promise<TimedPostResult>;

/**
 * Envia o que está na caixa de saída. Sucesso (200/201) ou recusa definitiva do servidor (4xx fora 408/429)
 * tira o item da fila; falha de rede ou 5xx deixa para tentar de novo. Devolve os ids das atividades criadas.
 */
export async function flushOutbox(s: Store, uid: string, post: TimedPost, now: number): Promise<Record<string, string>> {
  const done: Record<string, string> = {};
  for (const b of loadOutbox(s, uid, now)) {
    const res = await post(b);
    const ok = res.status === 200 || res.status === 201;
    const final = res.status >= 400 && res.status < 500 && res.status !== 408 && res.status !== 429;
    if (ok && res.activityId) done[b.client_id] = res.activityId;
    if (ok || final) removeFromOutbox(s, uid, b.client_id, now);
  }
  return done;
}
